import { useContext, useCallback, useRef } from "react";

import AppContext from "../store/app-context";
import ReaderContext from "../store/reader-context";
import { barcodesMatch, barcodeShortLabel } from "../utils/barcode-format";
import useCameraPermission from "./use-camera-permission";

import Quagga from 'quagga';

const useReader = (callback: any) => {
    const appCtx = useContext(AppContext);
    const readerCtx = useContext(ReaderContext);
    const { checkCamera, requestCameraAccess, isDismissedThisSession } = useCameraPermission();

    const handlerRef = useRef<any>(null);

    // Extract stable references
    const cameraSettings = appCtx.settings.camera;
    const locationLength = appCtx.location.length;
    const updateLocation = appCtx.updateLocation;
    const updateStarted = readerCtx.updateStarted;
    const updateConfig = readerCtx.updateConfig;
    const updateMessages = readerCtx.updateMessages;
    const updateCameraBlocked = readerCtx.updateCameraBlocked;
    const sounds = readerCtx.sounds;

    const startQuagga = useCallback(async (config:any): Promise<boolean> => {
        console.log('startQuagga');

        // Reset blocked state at the start of each attempt
        updateCameraBlocked(false);

        // --- Camera permission gate ---
        const permState = await checkCamera();

        if (permState === 'denied') {
            updateCameraBlocked(true);
            // Show the scanner overlay so the modal can render inside it
            updateStarted(true);
            updateConfig(config);
            if (isDismissedThisSession()) {
                appCtx.newMessage({ text: 'Camera access is blocked. Check your browser or device settings.', type: 0 });
            }
            return false;
        }

        if (permState === 'unsupported') {
            // iOS Safari — permissions API unavailable, probe via getUserMedia
            const hasAccess = await requestCameraAccess();
            if (!hasAccess) {
                updateCameraBlocked(true);
                updateStarted(true);
                updateConfig(config);
                if (isDismissedThisSession()) {
                    appCtx.newMessage({ text: 'Camera access is blocked. Check your browser or device settings.', type: 0 });
                }
                return false;
            }
            // Small delay to release the probe stream before Quagga requests its own
            await new Promise(r => setTimeout(r, 200));
        }

        if (permState === 'prompt') {
            updateMessages('add', {
                type: 'info',
                text: 'Allow camera access when prompted by your browser.'
            });
        }

        // --- Quagga init ---
        const quaggaTarget:any = document.querySelector('#quagga');

        return new Promise<boolean>((resolve) => {
            Quagga.init({
                inputStream : {
                    name : "Live",
                    type : "LiveStream",
                    target: quaggaTarget,
                    constraints: {
                        width: 1280,
                        height: 720,
                        facingMode: "environment",
                        deviceId: cameraSettings
                    }
                },
                locator: {
                    patchSize: "medium",
                    halfSample: false
                },
                decoder : {
                    readers : [
                        "code_128_reader",
                        "code_39_reader",
                        "ean_reader",
                        "ean_8_reader"
                    ]
                }
            }, function(err:any) {
                if (err) {
                    console.log({err});
                    // Detect permission denial from the Quagga error
                    const errName = err?.name || err?.message || '';
                    if (errName === 'NotAllowedError' || (typeof errName === 'string' && errName.includes('Permission'))) {
                        updateCameraBlocked(true);
                        updateStarted(true);
                        updateConfig(config);
                    }
                    resolve(false);
                    return;
                }

                Quagga.start();
                updateStarted(true);
                updateConfig(config);

                const processBarcode = (data:any) => {
                    let barcode:string = '';

                    if (data && data.codeResult && data.codeResult.code)  barcode = data.codeResult.code.replace(/\*/g, '');

                    if(barcode.length > 0 ){
                        let regEx = new RegExp(/^\d+$/);
                        let isBox = regEx.test(barcode);

                        if(isBox){
                            let boxFound = false;
                            config.boxes.forEach((boxInList:any) => {
                                if(barcodesMatch(boxInList.barcode || '', barcode)){
                                    boxFound = true;
                                    const label = barcodeShortLabel(boxInList.barcode, boxInList.id, boxInList.barcodeComponents);
                                    if(!boxInList.scanned){
                                        boxInList.scanned = true;
                                        sounds.ok.play();
                                        updateMessages('add', {
                                            type: 'ok',
                                            text: label + ' scanned successfully \u2713'
                                        });
                                    }else{
                                        updateMessages('add', {
                                            type: 'warn',
                                            text: label + ' already scanned'
                                        });
                                        sounds.warn.play();
                                    }

                                    config.update(boxInList);
                                }
                            });

                            if(!boxFound) {
                                updateMessages('add', {
                                    type: 'error',
                                    text: barcodeShortLabel(barcode) + ' is not in this list. Please check you are scanning the correct boxes.'
                                });
                                sounds.error.play();
                            }
                        }else{
                            if(locationLength === 0){
                                sounds.ok.play();
                                updateLocation(barcode);
                                config.update(barcode);
                            }
                        }
                    }
                };

                const lastCallRef = { current: 0 };
                const debouncedProcessBarcode = (data: any) => {
                    const now = Date.now();
                    if (now - lastCallRef.current > 500) {
                        lastCallRef.current = now;
                        processBarcode(data);
                    }
                };
                handlerRef.current = debouncedProcessBarcode;

                Quagga.onDetected(debouncedProcessBarcode);
                resolve(true);
            });
        });
    }, [cameraSettings, locationLength, updateLocation, updateStarted, updateConfig, updateMessages, updateCameraBlocked, sounds, checkCamera, requestCameraAccess, isDismissedThisSession, appCtx]);

    const stopQuagga = useCallback(() => {
        console.log('stopQuagga');

        Quagga.stop();
        Quagga.offDetected(handlerRef.current);

        const quaggaTarget:any = document.querySelector('#quagga');
        quaggaTarget.innerHTML = '';

        updateStarted(false);
        updateCameraBlocked(false);
        updateConfig({ boxes: [], update: (box:any) => {} });
        updateMessages('clear', []);
    }, [updateStarted, updateCameraBlocked, updateConfig, updateMessages]);

    return {
        startQuagga,
        stopQuagga
    };
};

export default useReader;
